import { ToastCustom } from "@components";
import {
	type RefObject,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { toast } from "sonner";

const ANDROID_REGEX = /android/;

const isAndroid = () => {
	if (typeof window === "undefined") {
		return false;
	}
	const userAgent = window.navigator.userAgent.toLowerCase();
	return ANDROID_REGEX.test(userAgent);
};

type FacingMode = "user" | "environment";

type UseDamageCameraStreamParams = {
	videoRef: RefObject<HTMLVideoElement | null>;
	isLandscape: boolean;
};

type UseDamageCameraStreamResult = {
	stream: MediaStream | null;
	cameraError: string | null;
	isVideoReady: boolean;
	hasTriedStream: boolean;
	isFlashOn: boolean;
	isFlashSupported: boolean;
	currentFacingMode: FacingMode;
	setCameraError: (val: string | null) => void;
	setIsVideoReady: (val: boolean) => void;
	requestCameraPermission: () => Promise<boolean>;
	startCamera: () => Promise<boolean | void>;
	stopCamera: () => Promise<void>;
	toggleCamera: () => Promise<void>;
	toggleFlash: () => Promise<void>;
	setFlash: (enabled: boolean) => Promise<void>;
};

export function useDamageCameraStream({
	videoRef,
	isLandscape,
}: UseDamageCameraStreamParams): UseDamageCameraStreamResult {
	const [stream, setStream] = useState<MediaStream | null>(null);
	const [cameraError, setCameraError] = useState<string | null>(null);
	const [isVideoReady, setIsVideoReady] = useState(false);
	const [hasTriedStream, setHasTriedStream] = useState(false);
	const [isFlashOn, setIsFlashOn] = useState(false);
	const [isFlashSupported, setIsFlashSupported] = useState(false);
	const [currentFacingMode, setCurrentFacingMode] =
		useState<FacingMode>("environment");

	const facingMode = useRef<FacingMode>("environment");
	const isAndroidRef = useRef(isAndroid());
	const isStartingRef = useRef(false);
	const streamRef = useRef<MediaStream | null>(null);

	const requestCameraPermission = useCallback(async (): Promise<boolean> => {
		try {
			const primaryConstraints: MediaStreamConstraints = {
				video: {
					facingMode: { ideal: facingMode.current },
					width: { ideal: 1920 },
					height: { ideal: 1080 },
				},
				audio: false,
			};
			const fallbackConstraints: MediaStreamConstraints = {
				video: {
					facingMode: { ideal: facingMode.current },
					width: { ideal: 1280 },
					height: { ideal: 720 },
				},
				audio: false,
			};
			// Solicita permissão antes de entrar em fullscreen
			const testStream = await navigator.mediaDevices
				.getUserMedia(primaryConstraints)
				.catch(() => navigator.mediaDevices.getUserMedia(fallbackConstraints));

			// Fecha o stream imediatamente, pois só queremos verificar a permissão
			for (const track of testStream.getTracks()) {
				track.stop();
			}

			return true;
		} catch {
			toast(
				ToastCustom({
					title: "Acesso à câmera necessário",
					description:
						"Ative a permissão de câmera nas configurações do navegador para seguir com esta ação.",
				})
			);
			return false;
		}
	}, []);

	const startCamera = useCallback(async () => {
		if (!isLandscape) {
			return false;
		}

		// Evita múltiplas chamadas simultâneas
		if (isStartingRef.current) {
			return false;
		}

		// Se já existe um stream ativo e funcionando, não reinicia
		if (streamRef.current && streamRef.current.active) {
			const activeTracks = streamRef.current
				.getVideoTracks()
				.filter((track) => track.readyState === "live");
			if (activeTracks.length > 0) {
				return true;
			}
		}

		isStartingRef.current = true;
		setCameraError(null);
		setIsVideoReady(false);
		setHasTriedStream(true);

		try {
			// Para qualquer stream anterior antes de criar um novo
			if (streamRef.current) {
				const oldStream = streamRef.current;
				for (const track of oldStream.getTracks()) {
					if (track.readyState !== "ended") {
						track.stop();
					}
				}
			}

			// IMPORTANTE: não pedimos `torch` nos constraints do getUserMedia,
			// pois isso quebra em muitos Androids. O flash é aplicado depois
			// via applyConstraints, se suportado.
			const primaryConstraints: MediaStreamConstraints = {
				video: {
					facingMode: { ideal: facingMode.current },
					width: { ideal: 1920 },
					height: { ideal: 1080 },
				},
				audio: false,
			};
			const fallbackConstraints: MediaStreamConstraints = {
				video: {
					facingMode: { ideal: facingMode.current },
					width: { ideal: 1280 },
					height: { ideal: 720 },
				},
				audio: false,
			};
			const simpleConstraints: MediaStreamConstraints = {
				video: { facingMode: { ideal: facingMode.current } },
				audio: false,
			};

			let mediaStream: MediaStream | null = null;
			let torchSupported = false;

			// Android: percorre todas as câmeras e tenta achar uma com suporte a flash via deviceId
			// iOS/Safari: usa facingMode direto nos constraints (melhor suporte no Safari)
			if (isAndroidRef.current) {
				const devices = await navigator.mediaDevices.enumerateDevices();
				let videoDevices = devices.filter((d) => d.kind === "videoinput");

				if (facingMode.current === "environment") {
					// prioriza traseira
					videoDevices = [
						...videoDevices.filter((d) =>
							d.label.toLowerCase().includes("back")
						),
						...videoDevices.filter(
							(d) => !d.label.toLowerCase().includes("back")
						),
					];

					for (const device of videoDevices) {
						try {
							const testStream = await navigator.mediaDevices.getUserMedia({
								video: { deviceId: { exact: device.deviceId } },
								audio: false,
							});

							const track = testStream.getVideoTracks()[0];
							const caps = track.getCapabilities?.() as any;

							if (caps?.torch) {
								mediaStream = testStream;
								torchSupported = true;
								break;
							}

							if ("ImageCapture" in window) {
								try {
									const imageCapture = new ImageCapture(track);
									const photoCaps = await imageCapture.getPhotoCapabilities();
									if (photoCaps.fillLightMode?.includes("flash")) {
										mediaStream = testStream;
										torchSupported = true;
										break;
									}
								} catch {
									// ignora erros do ImageCapture
								}
							}

							// fecha se não servir
							for (const t of testStream.getTracks()) {
								t.stop();
							}
						} catch {
							setCameraError("Erro ao abrir câmera. Tente novamente.");
							toast(
								ToastCustom({
									title: "Erro ao abrir câmera",
									description:
										"Não foi possível acessar esta câmera. Verifique permissões e tente novamente.",
								})
							);
						}
					}
				} else {
					// frontal: abre direto e sai (não precisa de torch)
					mediaStream = await navigator.mediaDevices.getUserMedia({
						video: { facingMode: { ideal: "user" } },
						audio: false,
					});
				}
			} else {
				// iOS/Safari: usa facingMode direto
				try {
					mediaStream =
						await navigator.mediaDevices.getUserMedia(primaryConstraints);
				} catch {
					try {
						mediaStream =
							await navigator.mediaDevices.getUserMedia(fallbackConstraints);
					} catch {
						mediaStream =
							await navigator.mediaDevices.getUserMedia(simpleConstraints);
					}
				}
			}

			// Se nenhuma câmera suportar flash, cai no fluxo normal
			if (!mediaStream) {
				try {
					if (isAndroidRef.current) {
						mediaStream =
							await navigator.mediaDevices.getUserMedia(simpleConstraints);
					} else {
						mediaStream =
							await navigator.mediaDevices.getUserMedia(primaryConstraints);
					}
				} catch {
					try {
						mediaStream =
							await navigator.mediaDevices.getUserMedia(fallbackConstraints);
					} catch {
						mediaStream =
							await navigator.mediaDevices.getUserMedia(simpleConstraints);
					}
				}
			}

			// Atualiza refs e estado
			streamRef.current = mediaStream;
			setStream(mediaStream);

			if (videoRef.current) {
				if (videoRef.current.srcObject) {
					videoRef.current.srcObject = null;
				}
				videoRef.current.srcObject = mediaStream;

				const playPromise = videoRef.current.play();
				if (playPromise !== undefined) {
					await playPromise;
				}
			}

			const videoTrack = mediaStream.getVideoTracks()[0];

			const checkCapabilities = async (attempts = 3) => {
				for (let i = 0; i < attempts; i++) {
					if ("getCapabilities" in videoTrack) {
						const capabilities =
							videoTrack.getCapabilities?.() as MediaTrackCapabilities & {
								torch?: boolean;
							};

						if (capabilities?.torch) {
							return true;
						}
					}
					await new Promise((resolve) => setTimeout(resolve, 300));
				}
				return false;
			};

			if (videoTrack && "getCapabilities" in videoTrack) {
				const capabilities = videoTrack.getCapabilities?.();

				if (capabilities?.width && capabilities?.height) {
					try {
						await videoTrack.applyConstraints({
							width: { ideal: 1920, max: capabilities.width.max },
							height: { ideal: 1080, max: capabilities.height.max },
						});
					} catch {
						// ignora falhas nos constraints de resolução
					}
				}
			}

			if (!torchSupported) {
				torchSupported = await checkCapabilities();
			}

			if (!torchSupported && "ImageCapture" in window) {
				try {
					const imageCapture = new ImageCapture(videoTrack);
					const photoCapabilities = await imageCapture.getPhotoCapabilities();
					if (photoCapabilities.fillLightMode?.includes("flash")) {
						torchSupported = true;
					}
				} catch {
					torchSupported = false;
				}
			}

			setIsFlashSupported(torchSupported);

			// Aplica o flash após obter o stream, se necessário
			if (
				isFlashOn &&
				facingMode.current === "environment" &&
				torchSupported &&
				videoTrack &&
				"applyConstraints" in videoTrack
			) {
				try {
					await videoTrack.applyConstraints({
						advanced: [{ torch: true } as MediaTrackConstraints],
					});
				} catch {
					setIsFlashSupported(false);
					setIsFlashOn(false);
				}
			}

			setIsVideoReady(true);
			isStartingRef.current = false;
			return true;
		} catch (_error) {
			isStartingRef.current = false;
			setCameraError(
				"Não foi possível acessar a câmera. Verifique permissões e tente novamente."
			);
			setStream(null);
			streamRef.current = null;
			setIsVideoReady(false);
			return false;
		}
	}, [isLandscape, isFlashOn, videoRef]);

	const stopCamera = useCallback(async () => {
		// Usa o ref para garantir que estamos parando o stream correto
		const currentStream = streamRef.current || stream;

		// Desliga o flash antes de parar o stream
		if (isFlashOn && currentStream && isFlashSupported) {
			const videoTrack = currentStream.getVideoTracks()[0];
			if (videoTrack && "applyConstraints" in videoTrack) {
				try {
					await videoTrack.applyConstraints({
						advanced: [{ torch: false } as MediaTrackConstraints],
					});
				} catch {
					// Ignora erros ao desligar flash
				}
			}
		}

		// Para o vídeo primeiro
		if (videoRef.current) {
			try {
				videoRef.current.pause();
			} catch {
				// ignora erros ao pausar
			}
			videoRef.current.srcObject = null;
			// força recarregar o elemento de vídeo
			try {
				videoRef.current.load();
			} catch {
				// ignora se não suportado
			}
		}

		// Para todos os tracks do stream
		if (currentStream) {
			const tracks = currentStream.getTracks();
			for (const track of tracks) {
				if (track.readyState !== "ended") {
					track.stop();
				}
			}

			// Garante que todos os tracks realmente pararam
			for (const track of tracks) {
				if (track.readyState !== "ended") {
					track.stop();
				}
			}
		}

		// Limpa refs e estado
		streamRef.current = null;
		setStream(null);
		setIsVideoReady(false);
		setHasTriedStream(false);
		setIsFlashOn(false);
		isStartingRef.current = false;
	}, [stream, isFlashOn, isFlashSupported, videoRef]);

	const isTogglingRef = useRef(false);

	const toggleCamera = useCallback(async () => {
		// evita múltiplos toggles concorrentes (problema comum em Android)
		if (isTogglingRef.current || isStartingRef.current) {
			return;
		}
		isTogglingRef.current = true;

		try {
			setCameraError(null);

			// Desliga o flash antes de alternar, se estiver ligado
			const currentStream = streamRef.current || stream;
			if (isFlashOn && currentStream && isFlashSupported) {
				const videoTrack = currentStream.getVideoTracks()[0];
				if (videoTrack && "applyConstraints" in videoTrack) {
					try {
						await videoTrack.applyConstraints({
							advanced: [{ torch: false } as MediaTrackConstraints],
						});
					} catch {
						// Ignora erros ao desligar flash
					}
				}
			}

			// Para o stream atual antes de alternar
			await stopCamera();

			// Alterna o facingMode
			const newFacingMode =
				facingMode.current === "user" ? "environment" : "user";
			facingMode.current = newFacingMode;
			setCurrentFacingMode(newFacingMode);

			// Desliga o flash se estiver ligado (flash só funciona na câmera traseira)
			if (isFlashOn && newFacingMode === "user") {
				setIsFlashOn(false);
			}

			// Aguarda um pouco para garantir que o stream foi completamente parado
			// Android precisa de mais tempo

			// Reinicia a câmera com o novo facingMode; em Android, tenta mais de uma vez
			let started = await startCamera();
			if (!started && isAndroidRef.current) {
				started = await startCamera();
			}
			if (!started && isAndroidRef.current) {
				started = await startCamera();
			}

			if (!started) {
				setCameraError("Não foi possível alternar a câmera. Tente novamente.");
			}
		} catch (error) {
			setCameraError(
				"Erro ao alternar câmera. Tente novamente ou feche e abra a câmera."
			);
		} finally {
			isTogglingRef.current = false;
		}
	}, [stopCamera, startCamera, isFlashOn, stream, isFlashSupported]);

	const setFlash = useCallback(
		async (enabled: boolean) => {
			// Flash só funciona na câmera traseira e em dispositivos compatíveis
			if (currentFacingMode === "user" || !isFlashSupported) {
				return;
			}

			// Evita reprocessar se já estiver no estado desejado
			if (enabled === isFlashOn) {
				return;
			}

			setIsFlashOn(enabled);

			const currentStream = streamRef.current || stream;
			if (!currentStream) {
				return;
			}

			const videoTrack = currentStream.getVideoTracks()[0];
			if (!(videoTrack && "applyConstraints" in videoTrack)) {
				return;
			}

			try {
				await videoTrack.applyConstraints({
					advanced: [{ torch: enabled } as MediaTrackConstraints],
				});
			} catch {
				// Reverte se falhar
				setIsFlashOn(!enabled);
				setIsFlashSupported(false);
			}
		},
		[currentFacingMode, isFlashSupported, isFlashOn, stream]
	);

	const toggleFlash = useCallback(async () => {
		await setFlash(!isFlashOn);
	}, [setFlash, isFlashOn]);

	// Listener para detectar quando o stream é encerrado inesperadamente
	useEffect(() => {
		const currentStream = streamRef.current || stream;
		if (!currentStream) {
			return;
		}

		const handleTrackEnded = () => {
			const tracks = currentStream.getVideoTracks();
			const allEnded = tracks.every((track) => track.readyState === "ended");
			if (allEnded && !isStartingRef.current && !isTogglingRef.current) {
				// Stream foi encerrado inesperadamente
				setStream(null);
				streamRef.current = null;
				setIsVideoReady(false);
				setCameraError(
					"A câmera foi encerrada inesperadamente. Tente novamente."
				);
			}
		};

		const tracks = currentStream.getVideoTracks();
		for (const track of tracks) {
			track.addEventListener("ended", handleTrackEnded);
		}

		return () => {
			for (const track of tracks) {
				track.removeEventListener("ended", handleTrackEnded);
			}
		};
	}, [stream]);

	// Cleanup no unmount
	useEffect(() => {
		return () => {
			// Para qualquer stream ativo
			const currentStream = streamRef.current;
			if (currentStream) {
				for (const track of currentStream.getTracks()) {
					if (track.readyState !== "ended") {
						track.stop();
					}
				}
				streamRef.current = null;
			}

			// Limpa o vídeo
			if (videoRef.current) {
				try {
					videoRef.current.pause();
					videoRef.current.srcObject = null;
				} catch {
					// ignora erros
				}
			}
		};
	}, [videoRef]);

	return {
		stream,
		cameraError,
		isVideoReady,
		hasTriedStream,
		isFlashOn,
		isFlashSupported,
		currentFacingMode,
		setCameraError,
		setIsVideoReady,
		requestCameraPermission,
		startCamera,
		stopCamera,
		toggleCamera,
		toggleFlash,
		setFlash,
	};
}
